/**
 * Seite „Kurssicherung & Zahlungsbedarf" - 1:1-Portierung von `renderPayPage`
 * (`index.html:13504-13572`) samt ihren beiden Zulieferern `fxWeekChartHtml` (13420) und
 * `fxLedgerEntry` (13457). Klassennamen, Reihenfolge, Texte und Rechenwege sind woertlich
 * uebernommen; ergaenzt wurden ausschliesslich Typen und der Umbau der DB-Bindung auf Props.
 *
 * ================= ⚠ Der Brief-Bereich enthaelt eine TOTE zweite Fassung =================
 *
 * Der Task-Brief nennt als Herkunft `index.html:13016-13691`. Darin steht bei 13036
 * `renderPayPageLegacy` - eine vollstaendige zweite Fassung derselben Seite (153 Zeilen) mit dem
 * Titel „Zahlungen & Kurssicherung", einer Fixstatus-Tabelle, einem Monatsdiagramm und der
 * Klasse `.fx-legend`. Sie hat **genau ein Vorkommen in der ganzen Datei: ihre eigene Definition.**
 * Nie aufgerufen, toter Code.
 *
 * Portiert ist ausschliesslich die LEBENDE Fassung `renderPayPage` (14 Aufrufstellen). Mehrere
 * Zusicherungen des Briefs beschreiben die tote - sie haetten nichts unterschieden; alle sind im
 * Task-Bericht unter „Befunde am Brief" einzeln aufgefuehrt.
 *
 * ================= Der Umbau: globales DB -> Props =================
 *
 * Das Original liest `DB.orders`, `DB.fxDeals` und `DB.suppliers` aus dem globalen Zustand, den
 * Entwurfszustand aus der globalen `let fxCalculatorDraft` (index.html:4596) und den angemeldeten
 * Nutzer aus `CURRENT_USER`; es schreibt sein Ergebnis in `#page-payments`. Hier kommen alle
 * Datenlisten als Props (`bestellungen`, `deals`, `lieferanten`), die Statusdefinitionen als
 * `definitionen`, der Entwurf als `entwurf`/`onEntwurf` und die Rechte als `rechte`.
 *
 * `byId(id)` (index.html:4667, liest `DB.orders`) wird zu `bestellungenNach(bestellungen, id)`.
 *
 * ================= `ensureFxCalculatorDraft` ist Teil des Renderns =================
 *
 * `renderPayPage` ruft in seiner ERSTEN Zeile `ensureFxCalculatorDraft()` (13226) auf: ist der
 * globale Entwurf `null`, wird er aus der zuletzt gesicherten gueltigen Sicherung vorbelegt
 * (alter Kontostand = deren `newBalance`, alter Kurs = deren `mixedRate`, Bank uebernommen,
 * Datum = heute). Ohne diesen Schritt zeigte die Maske leere Felder, wo der Bestand vorbelegt.
 *
 * Deshalb ist die Vorbelegung hier als `ensureFxCalculatorEntwurf(deals)` mitportiert - **ohne
 * Seiteneffekt** (das Original schreibt in die globale Variable, hier ist es eine reine Funktion).
 * Sie greift bei `entwurf == null`, genau wie das Original bei `fxCalculatorDraft === null`; ein
 * uebergebenes `{}` bleibt unangetastet (`{}` ist wahrheitswertig, das Original gaebe es ebenso
 * unveraendert zurueck). Der Behaelter braucht sie fuer den Speicherweg mit - deshalb exportiert.
 *
 * ================= Was NICHT hierher gehoert (und warum) =================
 *
 * `saveFxCalculatorDeal` (13301), `clearFxCalculator` (13268), `markFxCreditTask` (13335),
 * `patchFxDeal` (13618), `bindFx` (13634), `unbindFx` (13654), `removeFxDeal` (13663),
 * `uploadFxDoc` (13674) und `removeFxDoc` (13681) stehen im Bestand AUSSERHALB von `renderPayPage`.
 * Sie aendern `DB`, rufen `save()` und zeichnen anschliessend neu - das ist Behaelterarbeit. Hier
 * gehen sie als Rueckrufe hinaus; die Seite bleibt eine reine Darstellung.
 *
 * **Die eine Ausnahme, benannt - und seither durch die Nachtrag-Rechteregel ERSETZT:** die ERSTE
 * Zeile von `saveFxCalculatorDeal` (13302) und von `removeFxDeal` (13664) ist im BESTAND je eine
 * Rechtepruefung, die mit einem `toast` endet und den Rest nicht ausfuehrt - der Knopf bleibt dort
 * BEDIENBAR (13546), er weist erst beim Klick ab. Diese Datei folgte dem zunaechst ebenso (die
 * Pruefung wanderte in den Klick-Handler, der Knopf blieb FREIGEGEBEN). Die Nachtrag-Rechteregel
 * (Auftraggeber-Entscheidung, Task-8-Brief des Verdrahtungsplans, 2026-08-11) hat diesen Weg seither
 * ERSETZT: beide Knoepfe sind jetzt ohne `manage_fx` `disabled` mit `title="fehlende Berechtigung"`
 * (`MischkursRechner.tsx#gesperrt`, `SicherungDetail.tsx` (frueher `SicherungsListe`)) - eine bewusste Abweichung vom
 * Bestand, kein Klick-Meldeweg mehr. `onHinweis` bleibt Teil der Props (Begruendung dort), hat an
 * diesen beiden Stellen aber keinen Aufrufer mehr. Es traegt nur noch die Bestaetigungen der
 * Kurssicherungsuebersicht (Toast); den Hinweis, wenn eine Sicherung aus der Tabelle noch nicht im Store
 * steht, meldet die Seite seit Fehleranzeige Paket 1, Task 9 selbst am Fehlerstapel (die Absicht, die
 * Sicherung zu oeffnen, wird nicht ausgefuehrt).
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. **Betragsvergleiche auf Wahrheitswert, nicht auf `!== 0`.** Der Bestand prueft an sechs
 *    Stellen `betrag ? … : …`; eine echte 0 nimmt dort den Ersatzzweig:
 *      - `values.mixedRate ? NUM4.format(…) : '–'` (KPI, 13526) und dasselbe im Eingabefeld (13533)
 *      - `values.newBalance ? … : ''` (Eingabefeld „Guthaben neu", 13532)
 *      - `Number(deal.rate) ? NUM4.format(…) : '—'` (Ledger-Kopf, 13471; Guthaben-Task, 13561)
 *      - `due ? fxWeekChartValue(due) : '–'` und `covered ? … : '–'` (Diagramm, 13445-13446)
 *    Eine gesicherte Summe von exakt 0 erscheint dort also als Gedankenstrich statt als „$ 0".
 *    **Gemeldet, nicht repariert** - fachlich ist 0 an jeder dieser Stellen ein gueltiger Betrag.
 *    Die Summen selbst (`fmtUsd(totalOpen)`, `fmtUsd(totalSecured)`, `fmtUsd(values.newBalance)`)
 *    laufen dagegen ungeprueft durch und zeigen „$ 0" - derselbe Sachverhalt, zwei Darstellungen.
 * 2. `values.newBalance || hasFxManualValue(draft.oldShareOverride)` (13537/13540) mischt einen
 *    Zahlen-Wahrheitswert mit einem Feldtest: bei Guthaben 0 UND ohne manuellen Wert bleibt das
 *    Anteilsfeld leer, mit manuellem Wert zeigt es ihn. Woertlich uebernommen.
 * 3. `Number(deal.newBalance ?? deal.guthabenNeu) || values.newBalance` (13462): `??` faellt nur
 *    bei `null`/`undefined` weiter, `||` danach aber auch bei 0 - ein gespeichertes Guthaben von
 *    exakt 0 wird durch den nachgerechneten Wert ersetzt. Woertlich uebernommen.
 * 4. Der Anleitungstext („Anleitung aus der Excel", vier Schritte, `.fx-ref`) ist Oberflaechentext
 *    und woertlich uebernommen - siehe `MischkursRechner.tsx`.
 *
 * ================= Verhaltensunterschied, den der Prop-Umbau erzwingt (gemeldet) ==========
 *
 * Im Bestand zeichnet `patchFxCalculator` (13244) die Seite **absichtlich nicht** neu, sondern setzt
 * nur fuenf abgeleitete Eingabefelder direkt (`set(...)`, und auch die nur, wenn das Feld nicht den
 * Fokus hat) - damit die Eingabe den Fokus behaelt. Die beiden KPI-Kacheln „Guthaben neu" und
 * „Mischkurs USD je EUR" bleiben dabei **stehen**, bis irgendetwas ein volles `renderPayPage()`
 * ausloest. Diese Seite ist dagegen eine reine Funktion ihres `entwurf`-Props: jede Eingabe
 * rechnet auch die Kacheln neu. Der Bestand zeigt also zwischenzeitlich veraltete Kacheln, die
 * Portierung nicht. Siehe Task-Bericht.
 */
import { useEffect, useRef, useState } from "react";
import { todayIso } from "../../lib/format.ts";
import type { FxAccount, FxOverview } from "../../api/kurssicherung.ts";
import {
  dealValid,
  type FxCalculatorDraft,
  type FxDeal,
} from "../../lib/fx.ts";
import {
  activeReportingOrders,
  type StatusDefinitionen,
} from "../../lib/status.ts";
import { orderCurrency } from "../../lib/suppliers.ts";
import type { Order, Supplier } from "../../lib/types.ts";
import { meldeFehler } from "../../state/fehlerStapel.ts";
import { Seitenkopf } from "../App/Seitenkopf.tsx";
import type { UploadStellen } from "../App/useUploadStellen.ts";
import { SicherungDetailDialog } from "./SicherungDetailDialog.tsx";
import { KurssicherungsUebersicht } from "./KurssicherungsUebersicht.tsx";
import type { FxSicherungAnlegenEingabe } from "./SicherungAnlegenDialog.tsx";

/** Der an einer Sicherung haengende Beleg (`deal.doc`, 13153/13488). */
export interface FxBeleg {
  name?: unknown;
  path?: unknown;
}

export interface ZahlungsSeiteProps {
  bestellungen: readonly Order[];
  deals: readonly FxDeal[] | null | undefined;
  lieferanten: readonly Supplier[] | null | undefined;
  definitionen: StatusDefinitionen;
  /**
   * Der Entwurfszustand der Rechnermaske. `null`/`undefined` entspricht dem Ausgangswert der
   * globalen `fxCalculatorDraft` und loest die Vorbelegung aus (siehe Kopfkommentar).
   */
  entwurf: FxCalculatorDraft | null | undefined;
  /** `patchFxCalculator(key, value)`, index.html:13244. */
  onEntwurf: (schluessel: string, wert: string) => void;
  rechte: ReadonlySet<string>;
  /** `saveFxCalculatorDeal()`, index.html:13301 - ohne dessen erste (Rechte-)Zeile. */
  onSpeichern: () => void;
  /** `openDrawer(id)`. */
  onOeffnen: (bestellungId: string) => void;

  // ---- Die uebrigen Rueckrufe der Flaeche. Optional, damit der im Brief gezeigte Aufruf mit
  // ---- genau neun Props unveraendert uebersetzt; ohne sie ist die Seite an diesen Stellen still.
  /** `clearFxCalculator()`, 13268. */
  onEntwurfLeeren?: () => void;
  /** `patchFxDeal(index, key, value)`, 13618. */
  onSicherungAendern?: (index: number, schluessel: string, wert: string) => void;
  /** `uploadFxDoc(index)`, 13674. */
  onBelegHochladen?: (index: number) => void;
  /** `removeFxDoc(index)`, 13681. */
  onBelegEntfernen?: (index: number) => void;
  /** `bindFx(index)`, 13634 - Auswahl und Betrag liest das Original beim Klick aus dem DOM. */
  onBinden?: (index: number, bestellungId: string, usd: number) => void;
  /** `unbindFx(index, allocationIndex)`, 13654. */
  onLoesen?: (index: number, zuordnungIndex: number) => void;
  /** `toast(text)` - die Meldung der beiden Rechtewaechter. */
  onHinweis?: (text: string) => void;
  /** Der atomar vom Server geladene Stand von Konto, Snapshots, Deals und EZB-Kursen. */
  overview?: FxOverview | null;
  /** Der letzte atomare Ladevorgang schlug fehl und es gibt noch keinen Stand; die Übersicht zeigt die Ladefehler-Fläche mit Wiederholungsweg. */
  overviewLadefehler?: boolean;
  /** Der Grund dahinter (das gefangene Fehlerobjekt); die Fläche macht daraus den Klartext. */
  overviewLadeursache?: unknown;
  /** "Erneut laden" der Ladefehler-Fläche. Das Promise wird erfüllt, sobald der Versuch zu Ende ist, und lehnt nie ab. */
  onOverviewNeuLaden?: () => Promise<void> | void;
  /**
   * Lädt die atomare FX-Übersicht nach einem gelungenen Schreiben (Sicherung anlegen/löschen/übernehmen, Kontostand erfassen)
   * erneut. `satz` meldet der Aufrufer in den Fehlerstapel, wenn das Nachladen scheitert; das Promise lehnt nie ab.
   */
  onOverviewNachSchreiben?: (satz: string) => Promise<void> | void;
  /** Legt eine offene Sicherung über den bestehenden FX-Deal-Anlageweg an. */
  onSicherungAnlegen?: (eingabe: FxSicherungAnlegenEingabe) => Promise<FxDeal>;
  /** Die USD-Konten (Kontenstamm, auch inaktive) fuer die Bankauswahl beim Bearbeiten einer Sicherung. */
  konten?: readonly FxAccount[];
  /** Löscht eine nicht übernommene Sicherung weich (mit optionalem Grund); wirft bei 409 mit Serverwortlaut. */
  onSicherungWeichLoeschen?: (deal: FxDeal, grund: string) => Promise<void>;
  /**
   * Der Upload-Store der App (Upload-Sperren, Spec 2026-10-20): Sperre und Fortschrittszeile des Belegs je Sicherung. Die Seite
   * reicht ihn an das Detail-Panel durch; sie wird beim Reiterwechsel abgebaut, der Lauf lebt im Store weiter.
   */
  uploadStellen?: UploadStellen;
}

/** `byId(id)`, index.html:4667 - im Original ueber das globale `DB.orders`. */
export function bestellungNach(
  bestellungen: readonly Order[],
  id: unknown,
): Order | undefined {
  return bestellungen.find((bestellung) => bestellung.id === id);
}

/**
 * `ensureFxCalculatorDraft()`, index.html:13226 - hier ohne Seiteneffekt (siehe Kopfkommentar).
 * Der `force`-Zweig des Originals (`addFxDeal`, 13612) entfaellt: er erzwingt dieselbe Vorbelegung,
 * die diese Funktion ohnehin immer liefert.
 */
export function ensureFxCalculatorEntwurf(
  deals: readonly FxDeal[] | null | undefined,
): FxCalculatorDraft {
  const latest = [...(deals || [])]
    .filter(dealValid)
    .sort((a, b) =>
      String(b.securedAt || b.date || "").localeCompare(String(a.securedAt || a.date || "")),
    )[0];
  return {
    oldBalance: latest ? (latest.newBalance ?? latest.guthabenNeu ?? "") : "",
    oldRate: latest ? (latest.mixedRate ?? latest.rate ?? "") : "",
    newUsd: "",
    newRate: "",
    newBalanceOverride: "",
    mixedRateOverride: "",
    weightingOverride: "",
    oldShareOverride: "",
    newShareOverride: "",
    date: todayIso(),
    bank: latest?.bank || "",
  };
}

export function ZahlungsSeite({
  bestellungen,
  deals,
  lieferanten,
  definitionen,
  entwurf,
  onEntwurf,
  rechte,
  onSpeichern,
  onOeffnen,
  onEntwurfLeeren,
  onSicherungAendern,
  onBelegHochladen,
  onBelegEntfernen,
  onBinden,
  onLoesen,
  onHinweis,
  overview = null,
  overviewLadefehler = false,
  overviewLadeursache,
  onOverviewNeuLaden,
  onOverviewNachSchreiben,
  onSicherungAnlegen,
  konten,
  onSicherungWeichLoeschen,
  uploadStellen,
}: ZahlungsSeiteProps) {
  const [aktiveKontoId, setAktiveKontoId] = useState<string | null>(null);
  // Das Detail-Panel "Sicherung S-n" (Zeilenklick in der Sicherungstabelle); gehalten wird die ID, die Daten kommen live aus `deals`.
  const [detailDealId, setDetailDealId] = useState<string | null>(null);
  const detailAusloeserRef = useRef<HTMLElement | null>(null);
  // Die Seite zeigt bewusst ALLE Bestellungen: Laender- und Transportart-Filter der Kopfzeile
  // gelten hier nicht (die Kopfzeile blendet die Chips auf dieser Seite aus, wie bei der Liquiditaetsplanung).
  // index.html:13507-13517
  const alleDeals = deals || [];
  const aktiveKonten = overview?.accounts ?? [];
  const kontoFilterGueltig = aktiveKontoId === null || aktiveKonten.some(
    (konto) => konto.account.id === aktiveKontoId,
  );
  const kontoFilter = kontoFilterGueltig ? aktiveKontoId : null;
  // Das wirksame Konto je Sicherung (Cutover-Zuordnung vor Konto am Deal) steht nur in der Uebersicht.
  const kontoNachDeal = new Map((overview?.deals ?? []).filter((deal) => deal.accountId).map((deal) => [String(deal.id), String(deal.accountId)]));
  const kontoAuswaehlen = (accountId: string) => setAktiveKontoId((aktuell) => aktuell === accountId ? null : accountId);
  const alleKontenAnzeigen = () => setAktiveKontoId(null);
  useEffect(() => {
    if (!kontoFilterGueltig) setAktiveKontoId(null);
  }, [kontoFilterGueltig]);
  // Das Panel gehoert einer Sicherung, die es noch gibt: wird sie geloescht (oder uebernommen und dadurch umgebaut), faellt es weg.
  const detailIndex = detailDealId === null ? -1 : alleDeals.findIndex((deal) => String(deal.id) === detailDealId && !deal.deletedAt);
  const detailDeal = detailIndex >= 0 ? { deal: alleDeals[detailIndex], index: detailIndex } : null;
  const detailSchliessen = () => {
    setDetailDealId(null);
    detailAusloeserRef.current?.focus();
  };
  const bindeZiele = activeReportingOrders(bestellungen, definitionen).filter(
    (order) => orderCurrency(order, lieferanten) === "USD",
  );

  return (
    <div className="ov-wrap">
      <Seitenkopf titel="Zahlungen & Kurssicherung" unterzeile="Sicherungen · Kontobewegungen · EZB-Kurse" />
      <KurssicherungsUebersicht overview={overview} ladefehler={overviewLadefehler} ladeursache={overviewLadeursache} rechte={rechte} onNeuLaden={onOverviewNeuLaden} onOverviewNachSchreiben={onOverviewNachSchreiben} onSicherungAnlegen={onSicherungAnlegen} onSicherungLoeschen={onSicherungWeichLoeschen} zeigeMeldung={onHinweis} aktiveKontoId={kontoFilter} onKontoAuswaehlen={kontoAuswaehlen} onAlleKontenAnzeigen={alleKontenAnzeigen} onSicherungOeffnen={(deal, ausloeser) => {
        // Die Tabelle liest die Uebersicht, das Panel den Store: fehlt die Sicherung dort noch, sichtbarer Hinweis statt stillem Nichts.
        if (!alleDeals.some((eintrag) => String(eintrag.id) === String(deal.id))) { meldeFehler("Die Sicherung ist noch nicht geladen. Bitte die Seite kurz neu laden."); return; }
        detailAusloeserRef.current = ausloeser;
        setDetailDealId(String(deal.id));
      }} />
      {/*
        Das Wochendiagramm "Kurssicherung nach Kalenderwoche" (frueher hier: `WochenKalender`/
        `WochenDiagramm`) ist in die Liquiditaetsplanung umgezogen (`LiquiditaetsPage/`); diese Seite
        bleibt die Kontoverwaltung. `lib/fx.ts#fxWeekBuckets` bleibt unveraendert (Paritaetstests).
      */}
      {/*
        Detail-Panel "Sicherung S-n": ersetzt die fruehere zweite Liste. Es traegt, was die Tabelle nicht kann (Beleg, Bindung
        an Bestellungen, Datum/Bank/Kurs/Betrag bearbeiten), und bekommt aus Befund W3 (Pruefrunde 2) bewusst die UNGEFILTERTE
        `bestellungen` (nur zur Namensaufloesung bestehender Zuordnungen); `bindeZiele` (NEUE Zuordnungen) ist ebenfalls ungefiltert.
      */}
      {detailDeal && (
        <SicherungDetailDialog
          deal={detailDeal.deal}
          index={detailDeal.index}
          konten={konten}
          kontoNachDeal={kontoNachDeal}
          bestellungen={bestellungen}
          bindeZiele={bindeZiele}
          rechte={rechte}
          onOeffnen={onOeffnen}
          onSicherungAendern={onSicherungAendern}
          onBelegHochladen={onBelegHochladen}
          onBelegEntfernen={onBelegEntfernen}
          onBinden={onBinden}
          onLoesen={onLoesen}
          onHinweis={onHinweis}
          uploadStellen={uploadStellen}
          onSchliessen={detailSchliessen}
        />
      )}
    </div>
  );
}
